import { useMemo } from "react";
import { useGridCellWrapper } from "./GridCellWrapperContext";
import { useSvgViewBoxZoom } from "../../hooks/useSvgViewBoxZoom";

export function GridCellWrapperViewer() {
    const {
        inputSvg,
        viewBoxAttr,
        cellSize,
        gridStroke,
        gridStrokeOpacity,
        markedFill,
        markedFillOpacity,
        wrapStroke,
        wrapStrokeWidth,
        wrapStrokeOpacity,
        wrapStrokeActive,
        wrapOutlineSegments,
        markedCells,
        gridCols,
        gridRows,
        canExport,
        handleDownload,
    } = useGridCellWrapper();

    const [vbX, vbY, vbW, vbH] = useMemo(() => {
        const parsed = viewBoxAttr.split(/\s+/).map(Number);
        if (parsed.length !== 4 || !parsed.every((n) => Number.isFinite(n))) {
            return [0, 0, 200, 200] as const;
        }
        return parsed as [number, number, number, number];
    }, [viewBoxAttr]);

    const base = useMemo(
        () => ({ x: vbX, y: vbY, w: vbW, h: vbH }),
        [vbX, vbY, vbW, vbH],
    );

    const {
        svgRef,
        viewBoxAttr: viewBox,
        panHandlers,
        zoomPercent,
        zoomIn,
        zoomOut,
        resetView,
    } = useSvgViewBoxZoom({ base });

    const sourceInner = inputSvg
        ? inputSvg.content.replace(/<\/?svg[^>]*>/g, "")
        : "";

    return (
        <div className="panel-viewer">
            <div className="path-tool-preview">
                <div className="svg-zoom-controls">
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={zoomOut}
                        title="Zoom out"
                    >
                        −
                    </button>
                    <span className="zoom-level">{zoomPercent}%</span>
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={zoomIn}
                        title="Zoom in"
                    >
                        +
                    </button>
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={resetView}
                        title="Reset zoom"
                    >
                        ⌂
                    </button>
                </div>
                <div className="svg-scroll-wrapper">
                    {!inputSvg ? (
                        <div className="svg-placeholder">
                            <p>Upload an SVG to generate the wrapped grid.</p>
                        </div>
                    ) : (
                        <div className="svg-container svg-container--fill">
                            <svg
                                ref={svgRef}
                                viewBox={viewBox}
                                xmlns="http://www.w3.org/2000/svg"
                                className="path-tool-svg"
                                style={{ touchAction: "none" }}
                                {...panHandlers}
                            >
                                <g className="grid-cell-wrapper-marked">
                                    {markedCells.map((c, idx) => (
                                        <rect
                                            key={`${c.x},${c.y},${idx}`}
                                            x={c.x}
                                            y={c.y}
                                            width={cellSize}
                                            height={cellSize}
                                            fill={markedFill}
                                            fillOpacity={markedFillOpacity}
                                        />
                                    ))}
                                </g>
                                <g className="grid-cell-wrapper-lines">
                                    {Array.from({ length: gridCols + 1 }, (_, i) => {
                                        const x = vbX + i * cellSize;
                                        return (
                                            <line
                                                key={`vx-${x}`}
                                                x1={x}
                                                y1={vbY}
                                                x2={x}
                                                y2={vbY + vbH}
                                                stroke={gridStroke}
                                                strokeOpacity={gridStrokeOpacity}
                                                strokeWidth={1}
                                            />
                                        );
                                    })}
                                    {Array.from({ length: gridRows + 1 }, (_, i) => {
                                        const y = vbY + i * cellSize;
                                        return (
                                            <line
                                                key={`hy-${y}`}
                                                x1={vbX}
                                                y1={y}
                                                x2={vbX + vbW}
                                                y2={y}
                                                stroke={gridStroke}
                                                strokeOpacity={gridStrokeOpacity}
                                                strokeWidth={1}
                                            />
                                        );
                                    })}
                                </g>
                                {wrapStrokeActive && wrapOutlineSegments.length > 0 ? (
                                    <g
                                        className="grid-cell-wrapper-wrap-outline"
                                        fill="none"
                                        stroke={wrapStroke}
                                        strokeWidth={wrapStrokeWidth}
                                        strokeOpacity={wrapStrokeOpacity}
                                        strokeLinecap="square"
                                        strokeLinejoin="miter"
                                    >
                                        {wrapOutlineSegments.map((s, i) => (
                                            <line
                                                key={`wo-${s.x1}-${s.y1}-${s.x2}-${s.y2}-${i}`}
                                                x1={s.x1}
                                                y1={s.y1}
                                                x2={s.x2}
                                                y2={s.y2}
                                            />
                                        ))}
                                    </g>
                                ) : null}
                                <g
                                    className="grid-cell-wrapper-source"
                                    dangerouslySetInnerHTML={{ __html: sourceInner }}
                                />
                            </svg>
                        </div>
                    )}
                </div>
                <div className="path-tool-footer">
                    <p className="path-tool-meta">
                        {inputSvg
                            ? `Grid ${gridCols}×${gridRows} · Marked ${markedCells.length} cells.`
                            : "No SVG loaded."}
                    </p>
                    <button
                        type="button"
                        className="generic-button"
                        disabled={!canExport}
                        onClick={handleDownload}
                    >
                        Download result SVG
                    </button>
                </div>
            </div>
        </div>
    );
}
